import { useState } from 'react'
import { DateTime } from 'luxon'

import { Alert, Button, Card, Dialog, Text, Textarea, CardColors } from '@/ui'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'
import { centsToDollars } from '@/utils/currencyHelpers'
import {
  UPDATE_USER_TRANSACTION_KEY,
  updateUserTransaction,
} from '../transactions.slice'
import { TransactionReceiptsPanel } from './TransactionReceiptsPanel'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import {
  invalidateFetch,
  selectErrorsForKeys,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles, TextColors } from '@/styles'

export const TransactionNoteModal = ({
  transaction,
  followUpYear,
}: {
  transaction: TransactionOrSplit
  followUpYear?: string
}) => {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [transactionNote, setTransactionNote] = useState(
    transaction.notes || ''
  )
  const updating = useReselector(selectIsFetchingForKeys, [
    UPDATE_USER_TRANSACTION_KEY,
  ])
  const errors = useReselector(selectErrorsForKeys, [
    UPDATE_USER_TRANSACTION_KEY,
  ])
  const saveNote = async () => {
    dispatch(invalidateFetch(UPDATE_USER_TRANSACTION_KEY))

    const updateTransactionObject: {
      notes: string
      requestedClarificationAt?: string | null
      notifiedRequestedClarificationAt?: string | null
      notesLastUpdatedAt?: string
    } = {
      notes: transactionNote,
      notesLastUpdatedAt: new Date().toISOString(),
    }

    if (followUpYear) {
      updateTransactionObject.requestedClarificationAt = null
      updateTransactionObject.notifiedRequestedClarificationAt = null
    }

    const updated = await updateUserTransaction(
      transaction.id,
      updateTransactionObject,
      followUpYear
    )(dispatch)

    if (updated) {
      setOpen(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <Button variant="link">
          {transaction?.notes ? 'Edit Note' : 'Add Note'}
        </Button>
      </Dialog.Trigger>

      <Dialog.Content className="max-w-md">
        <Dialog.Title>
          {transaction?.notes ? 'Edit Your Note' : 'Add A Note'}
        </Dialog.Title>

        <div className="flex flex-col gap-6 mt-4">
          {/* Error Alert */}
          {errors.length > 0 && (
            <Alert
              variant="error"
              description="There was an error saving this note! Please try again."
            />
          )}

          {/* Transaction Summary Card */}
          <Card color={CardColors.LEVEL_0}>
            <div className="flex flex-col gap-2">
              <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
                Transaction
              </Text>
              <Text variant={TypographyStyles.BODY_SM}>
                Date:{' '}
                {DateTime.fromISO(transaction.date).toFormat(
                  DATE_FORMATS_LUXON.DISPLAY_SHORT
                )}
              </Text>
              <Text variant={TypographyStyles.BODY_SM}>
                Description: {transaction.description}
              </Text>
              <Text variant={TypographyStyles.BODY_SM}>
                Amount:{' '}
                <CurrencyFormatLabel
                  value={centsToDollars(transaction.amountInCents)}
                />
              </Text>
            </div>
          </Card>

          {/* Note Input */}
          <div className="flex flex-col gap-2">
            <Text variant={TypographyStyles.H3}>Note</Text>
            <Textarea
              rows={4}
              placeholder="What was this for? How was this used for business purposes? Please be descriptive as possible."
              value={transactionNote}
              onChange={(e) => setTransactionNote(e.target.value)}
              required
            />
          </div>

          {/* Receipt Attachment */}
          <div className="flex flex-col gap-2">
            <Text variant={TypographyStyles.H3}>Attach Receipt</Text>
            <Text variant={TypographyStyles.BODY_SM}>
              Attaching a receipt is highly recommended. This helps us
              categorize appropriately.
            </Text>
            <TransactionReceiptsPanel transaction={transaction} />
          </div>
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-4">
          <Dialog.Close asChild>
            <Button variant="link">Cancel</Button>
          </Dialog.Close>
          <Dialog.Close asChild>
            <Button onClick={saveNote} disabled={updating} loading={updating}>
              Save Note
            </Button>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
